import { useAppContext } from '@/context/AppContext';
import md5 from "crypto-js/md5";
import { useEffect, useRef } from 'react';

export const validateErrorsToObject = (value) => {
  const errors = {};

  value.forEach((item) => {
    errors[item.field] = item.reason;
  });

  return errors;
};

type Options = RequestInit & { noAbort?: boolean }

type handleAxiosErrorProp = {
  setErrors?: any
  toast: any
  t?: any
  onError?: any
}

export const handleApiErr = (e, { setErrors, toast, t, onError }: handleAxiosErrorProp) => {
  if (typeof t === 'undefined') {
    t = (m) => m
  }
  const resGlobal = (e) => {
    toast.error(e.message ? t(e.message) : t("connection_error"));
  };

  if (e.code !== 200) {
    //@ts-ignore
    if (e?.errors && typeof setErrors === "function") {
      //@ts-ignore
      setErrors(validateErrorsToObject(e?.errors));
    } else {
      //@ts-ignore
      if (e?.message) {
        if (typeof onError === "function") {
          //@ts-ignore
          if (!onError(e?.message, e.code)) {
            resGlobal(e);
          }
        } else {
          resGlobal(e);
        }
      } else {
        resGlobal(e);
      }
    }
  }
}

export const useAPI = (locale = 'en') => {
  const { kind, host, refreshTokenRef } = useAppContext()
  const abortRef = useRef<Map<AbortController, boolean>>(new Map())
  let baseURL = process.env.NEXT_PUBLIC_DOMAIN_APP ? 'https://' + process.env.NEXT_PUBLIC_DOMAIN_APP + '/api' : '/api';

  // if (process.env.NODE_ENV !== 'production') {
  //   baseURL = '/api'
  // }

  if (kind === "ADMIN") baseURL = baseURL + '/admin'
  if (kind === "RESELLER") baseURL = baseURL + '/reseller'

  const handleOptions = (method: string, body?: any, opts?: Options) => {
    const unix = Math.round(new Date().getTime() / 1000);
    const randString = (Math.random() + 1).toString(36).substring(6);
    const secret = process.env.NEXT_PUBLIC_DYNAMIC_SECRET;
    const key = process.env.NEXT_PUBLIC_DYNAMIC_KEY;
    const hashed = md5(`${unix},${randString},${secret},${key}`);

    const extraHeaders = opts?.headers || {}

    const options: Options = {
      credentials: 'include',
      headers: {
        "X-RPC-Lang": locale,
        'Accept': 'application/json',
        DS: unix + "," + randString + "," + hashed + "," + key,
        ...extraHeaders
      },
      method,
      noAbort: opts?.noAbort,
    }

    if (body instanceof FormData) {
      options.body = body
    } else if (body) {
      options.headers['Content-Type'] = 'application/json'
      options.body = JSON.stringify(body)
    } else {
      options.headers['Content-Type'] = 'application/json'
    }

    return options
  }

  const refreshToken = async () => {
    if (!refreshTokenRef.current) {
      refreshTokenRef.current = (async () => {
        try {
          await request('/refresh-token', handleOptions('POST', null, { noAbort: true }))
          await request('/refresh-token?step=2', handleOptions('POST', null, { noAbort: true }))
          await request('/refresh-token?step=3', handleOptions('POST', null, { noAbort: true }))
        } finally {
          refreshTokenRef.current = null
        }
      })()
    }
    return refreshTokenRef.current;
  }

  const request = async (url, options: Options) => {
    const ctrl = new AbortController()
    if (!options.noAbort) {
      abortRef.current.set(ctrl, true);
      options.signal = ctrl.signal
    }
    try {
      const r = await fetch(baseURL + url, options)
      if (!r.ok) {
        const data = await r.json()
        if (r.status === 401 && data.message === "Token is expired") {
          await refreshToken()
          return request(url, options)
        }
        return Promise.reject(data)
      }
      return await r.json()
    } catch (e) {
      return Promise.reject(e)
    } finally {
      if (options.noAbort) {
        abortRef.current.delete(ctrl)
      }
    }
  }

  useEffect(() => {
    return () => {
      abortRef.current.forEach((_, ctrl) => ctrl.abort("Umounted"));
      abortRef.current.clear();
    };
  }, []);

  const api = {
    get: (url, opts?) => request(url, handleOptions('GET', null, opts)),
    post: (url, body?, opts?) => request(url, handleOptions('POST', body, opts)),
    put: (url, body?, opts?) => request(url, handleOptions('PUT', body, opts)),
    patch: (url, body?, opts?) => request(url, handleOptions('PATCH', body, opts)),
    delete: (url, opts?) => request(url, handleOptions('DELETE', null, opts)),
    request,
    handleOptions,
  }

  return api
}

// export default api;
